import { 
  Search, Shield, Zap, Briefcase, Target, Rocket, Award
} from 'lucide-react';
import type { ParcoursVente } from './parcoursVentePRIsME';

export interface ParcoursClientParDuree extends Omit<ParcoursVente, 'niveau'> {
  niveau: 'strategique' | 'tactique' | 'operationnel' | 'mixte';
  dureeContrat: 'court-terme' | 'tactique' | 'strategique';
}

export const parcoursClientsParDuree: ParcoursClientParDuree[] = [
  {
    id: 'ct1',
    nom: '🔍 "L\'Éclaireur" - Diagnostic Express',
    description: 'Comprendre rapidement la posture TI actuelle et identifier les priorités',
    niveau: 'mixte',
    dureeContrat: 'court-terme',
    icon: Search,
    montant: {
      '25-30': '$2,500',
      '30-50': '$3,200',
      '50-75': '$4,000',
      '75-125': '$5,000',
      '125-250': '$6,500',
      '250+': '$8,000'
    },
    duree: '1-2 mois',
    livrables: [
      'Diagnostic TI complet',
      'Rapport d\'évaluation avec spider chart AS-IS',
      'Plan d\'action priorisé (quick wins)',
      'Recommandations opérationnelles immédiates'
    ],
    commentVendre: 'Monsieur le client, vous voulez comprendre rapidement où vous en êtes avec vos TI ? Ce parcours vous donne un diagnostic complet en 1-2 mois. C\'est comme faire un bilan de santé pour vos TI. Vous saurez exactement où sont vos forces et vos faiblesses, et vous aurez un plan d\'action priorisé pour les quick wins.',
    pourQui: 'Entreprises qui veulent comprendre rapidement leur posture TI actuelle sans s\'engager sur le long terme. Idéal pour les entreprises qui hésitent ou qui veulent tester avant de s\'engager.',
    objections: [
      {
        objection: '"On connaît déjà nos problèmes"',
        reponse: 'C\'est excellent ! Mais connaissez-vous TOUS vos problèmes ? Et surtout, savez-vous lesquels sont les plus critiques à résoudre en premier ? Ce diagnostic vous donne une vision complète et objective avec un plan d\'action priorisé. C\'est différent de "on pense connaître" vs "on MESURE objectivement".'
      },
      {
        objection: '"C\'est trop cher pour juste un rapport"',
        reponse: 'Ce n\'est pas "juste un rapport". C\'est un diagnostic complet avec spider chart, plan d\'action priorisé, et recommandations immédiates. C\'est comme aller chez le médecin : vous payez pour le diagnostic, pas juste pour le papier. Et ce diagnostic vous fait économiser de l\'argent en évitant de dépenser sur les mauvaises priorités.'
      }
    ],
    valeur: 'Le client comprend rapidement sa posture TI actuelle avec des données objectives. Il a un plan d\'action priorisé pour les quick wins et peut décider s\'il veut s\'engager sur le long terme.'
  },
  {
    id: 'ct2',
    nom: '🛡️ "Le Gardien" - Audit Cybersécurité',
    description: 'Évaluation complète de la posture cybersécurité selon NIST 2.0',
    niveau: 'mixte',
    dureeContrat: 'court-terme',
    icon: Shield,
    montant: {
      '25-30': '$3,000',
      '30-50': '$3,800',
      '50-75': '$4,800',
      '75-125': '$6,000',
      '125-250': '$7,500',
      '250+': '$9,500'
    },
    duree: '1-2 mois',
    livrables: [
      'Audit cybersécurité NIST 2.0 complet',
      'Plan de remédiation priorisé',
      'Politiques sécurité',
      'Rapport de conformité',
      'Recommandations immédiates'
    ],
    commentVendre: 'Monsieur le client, êtes-vous protégé contre les cybermenaces ? Ce parcours vous donne un audit cybersécurité complet selon NIST 2.0 en 1-2 mois. C\'est comme faire un audit de sécurité pour votre maison : vous saurez exactement où sont les failles et comment les corriger.',
    pourQui: 'Entreprises qui veulent évaluer leur posture cybersécurité rapidement. Idéal pour les entreprises qui traitent des données sensibles ou qui sont dans des secteurs réglementés.',
    objections: [
      {
        objection: '"On n\'a jamais eu de problème"',
        reponse: 'C\'est excellent ! Mais "jamais eu de problème" jusqu\'à présent, ce n\'est pas la même chose que "protégé contre les menaces". Ce parcours vous protège PROACTIVEMENT au lieu d\'attendre qu\'un problème arrive. C\'est comme avoir une alarme : vous ne l\'avez peut-être jamais utilisée, mais vous êtes content de l\'avoir quand vous en avez besoin.'
      },
      {
        objection: '"On a déjà de la sécurité"',
        reponse: 'C\'est excellent ! Mais avez-vous une STRATÉGIE de cybersécurité structurée selon NIST 2.0 ? Ce parcours structure votre cybersécurité de manière complète avec les 6 fonctions NIST. C\'est la différence entre "on a des outils" et "on a une stratégie complète".'
      }
    ],
    valeur: 'Le client a une évaluation complète de sa posture cybersécurité selon NIST 2.0 avec un plan de remédiation priorisé. Il sait exactement où sont ses risques et comment les corriger.'
  },
  {
    id: 'ct3',
    nom: '⚡ "L\'Accélérateur" - Quick Win TI',
    description: 'Interventions ciblées pour des résultats rapides et mesurables',
    niveau: 'mixte',
    dureeContrat: 'court-terme',
    icon: Zap,
    montant: {
      '25-30': '$2,800',
      '30-50': '$3,500',
      '50-75': '$4,500',
      '75-125': '$5,800',
      '125-250': '$7,200',
      '250+': '$9,000'
    },
    duree: '1-3 mois',
    livrables: [
      'Analyse des quick wins',
      'Plan d\'action priorisé',
      'Mise en œuvre d\'interventions ciblées',
      'Rapport de résultats'
    ],
    commentVendre: 'Monsieur le client, vous voulez voir des résultats rapides ? Ce parcours se concentre sur des améliorations rapides et mesurables en 1-3 mois. C\'est comme faire des rénovations rapides dans votre maison : vous voyez la valeur immédiatement.',
    pourQui: 'Entreprises qui veulent démontrer de la valeur rapidement. Idéal pour les entreprises qui veulent tester avant de s\'engager sur le long terme ou qui ont besoin de résultats rapides.',
    objections: [
      {
        objection: '"On veut une solution complète"',
        reponse: 'Je comprends ! Mais une solution complète prend du temps. Ce parcours vous donne des résultats RAPIDES pour démontrer la valeur, puis vous pouvez décider si vous voulez continuer avec une solution complète. C\'est comme tester avant d\'acheter.'
      },
      {
        objection: '"C\'est trop court"',
        reponse: 'C\'est justement l\'avantage ! Ce parcours se concentre sur les quick wins : des améliorations rapides et mesurables. Vous voyez la valeur immédiatement. Ensuite, si vous êtes satisfait, vous pouvez continuer avec un parcours plus long.'
      }
    ],
    valeur: 'Le client voit des résultats rapides et mesurables. Il démontre de la valeur immédiate et peut décider s\'il veut continuer avec une solution complète.'
  },
  {
    id: 't1',
    nom: '🗺️ "Le Cartographe" - Roadmap & Évolution',
    description: 'Roadmap annuelle complète avec suivi trimestriel de l\'évolution de la posture TI',
    niveau: 'mixte',
    dureeContrat: 'tactique',
    icon: Briefcase,
    montant: {
      '25-30': '$3,500/mois',
      '30-50': '$4,500/mois',
      '50-75': '$5,500/mois',
      '75-125': '$7,000/mois',
      '125-250': '$9,000/mois',
      '250+': '$11,500/mois'
    },
    duree: '1 an',
    livrables: [
      'Plan Directeur TI',
      'Roadmap annuelle détaillée',
      'Budget stratégique',
      'Cadre de gouvernance',
      'Matrice RACI',
      '4 révisions trimestrielles avec évolution de la posture TI'
    ],
    commentVendre: 'Monsieur le client, vous avez une vision stratégique ? Parfait ! Mais avez-vous un PLAN concret pour les 12 prochains mois ? Ce parcours vous donne votre roadmap annuelle complète avec suivi trimestriel. C\'est votre GPS pour l\'année, et vous voyez votre évolution trimestre par trimestre.',
    pourQui: 'Entreprises qui ont une vision stratégique mais qui manquent de planification tactique. Idéal pour les entreprises qui veulent structurer leurs projets TI et voir leur évolution.',
    objections: [
      {
        objection: '"On fait déjà des projets"',
        reponse: 'Faire des projets, c\'est bien. Mais sont-ils PRIORISÉS selon votre stratégie ? Ce parcours s\'assure que vous faites les BONS projets dans le BON ordre. Et surtout, vous voyez votre évolution trimestre par trimestre avec des ajustements proactifs.'
      },
      {
        objection: '"On a déjà une roadmap"',
        reponse: 'C\'est excellent ! Mais votre roadmap est-elle révisée trimestriellement avec suivi de l\'évolution de votre posture TI ? Ce parcours vous donne des révisions trimestrielles avec spider charts comparatifs. Vous voyez votre progression mesurable.'
      }
    ],
    valeur: 'Le client voit son évolution trimestre par trimestre avec des ajustements proactifs et une progression mesurable de sa posture TI. Il maximise son ROI en faisant les bons projets dans le bon ordre.'
  },
  {
    id: 't2',
    nom: '🎯 "Le Stratège" - ODS & Transformation',
    description: 'Offre de Service complète avec transformation structurée sur 1 an',
    niveau: 'mixte',
    dureeContrat: 'tactique',
    icon: Target,
    montant: {
      '25-30': '$4,000/mois',
      '30-50': '$5,200/mois',
      '50-75': '$6,500/mois',
      '75-125': '$8,200/mois',
      '125-250': '$10,500/mois',
      '250+': '$13,000/mois'
    },
    duree: '1 an',
    livrables: [
      'ODS complète (3 offres)',
      'Plan de transformation annuel',
      'Budget détaillé',
      'Suivi mensuel de progression',
      'Révisions trimestrielles',
      'Rapport d\'évolution annuel'
    ],
    commentVendre: 'Monsieur le client, vous voulez transformer vos TI de manière structurée ? Ce parcours vous donne une ODS complète avec transformation sur 1 an. Vous voyez votre transformation en temps réel avec des jalons clairs et une progression mesurable vers vos objectifs.',
    pourQui: 'Entreprises qui veulent transformer leurs TI de manière structurée. Idéal pour les entreprises qui ont besoin d\'une transformation complète avec suivi continu.',
    objections: [
      {
        objection: '"On veut juste l\'ODS"',
        reponse: 'Je comprends ! Mais une ODS sans suivi de transformation, c\'est comme avoir une recette sans cuisinier. Ce parcours vous donne l\'ODS ET le suivi pour la réaliser. Vous voyez votre transformation en temps réel avec des jalons clairs.'
      },
      {
        objection: '"C\'est trop complet"',
        reponse: 'C\'est justement l\'avantage ! Ce parcours vous donne une transformation COMPLÈTE avec suivi continu. Vous ne faites pas juste des projets, vous transformez votre entreprise de manière structurée et mesurable.'
      }
    ],
    valeur: 'Le client voit sa transformation en temps réel avec des jalons clairs et une progression mesurable vers ses objectifs. Il transforme son entreprise de manière structurée et soutenue.'
  },
  {
    id: 's1',
    nom: '🏆 "Le Visionnaire" - Transformation Durable',
    description: 'Vision long terme avec révision annuelle et suivi continu de l\'évolution de la posture TI',
    niveau: 'mixte',
    dureeContrat: 'strategique',
    icon: Rocket,
    montant: {
      '25-30': '$5,500/mois',
      '30-50': '$7,200/mois',
      '50-75': '$9,000/mois',
      '75-125': '$11,500/mois',
      '125-250': '$14,500/mois',
      '250+': '$18,000/mois'
    },
    duree: '2-3 ans',
    livrables: [
      'Plan Directeur TI 3-5 ans',
      'Roadmap annuelle (révisée chaque année)',
      'Budget stratégique multi-années',
      'Cadre de gouvernance',
      'Rapport d\'évolution annuel avec progression',
      'Révisions annuelles avec ajustements stratégiques'
    ],
    commentVendre: 'Monsieur le client, où voulez-vous être dans 3-5 ans ? Ce parcours vous donne une vision long terme avec révision annuelle. Vous voyez votre transformation sur plusieurs années avec une progression mesurable, des ajustements stratégiques annuels, et une vision claire de votre évolution.',
    pourQui: 'Entreprises qui veulent une vision long terme avec transformation durable. Idéal pour les entreprises qui veulent transformer leur modèle d\'affaires avec le digital.',
    objections: [
      {
        objection: '"C\'est trop long"',
        reponse: 'C\'est justement l\'avantage ! Une transformation durable prend du temps. Ce parcours vous donne une vision sur 3-5 ans avec révisions annuelles. Vous voyez votre évolution année sur année avec des ajustements stratégiques. C\'est ça, une vraie transformation.'
      },
      {
        objection: '"On veut juste le Plan Directeur"',
        reponse: 'Je comprends ! Mais un Plan Directeur sans suivi et révisions, c\'est comme avoir une carte sans GPS. Ce parcours vous donne le Plan Directeur ET les révisions annuelles avec suivi de l\'évolution. Vous voyez votre progression sur plusieurs années.'
      }
    ],
    valeur: 'Le client voit sa transformation sur plusieurs années avec une progression mesurable, des ajustements stratégiques annuels, et une vision claire de son évolution. Il transforme son entreprise de manière durable.'
  },
  {
    id: 's2',
    nom: '🚀 "L\'Excellence" - Partenariat Stratégique',
    description: 'Partenariat complet avec transformation continue et excellence opérationnelle',
    niveau: 'mixte',
    dureeContrat: 'strategique',
    icon: Award,
    montant: {
      '25-30': '$6,000/mois',
      '30-50': '$7,800/mois',
      '50-75': '$9,800/mois',
      '75-125': '$12,500/mois',
      '125-250': '$15,500/mois',
      '250+': '$19,500/mois'
    },
    duree: '2-3 ans',
    livrables: [
      'Tous les documents stratégiques + tactiques + opérationnels',
      'Comité TI structuré',
      'Portail PRIsME Client',
      'Support complet',
      'Rapport d\'évolution annuel avec progression détaillée',
      'Révisions annuelles avec ajustements',
      'Plan d\'amélioration continue'
    ],
    commentVendre: 'Monsieur le client, voulez-vous un partenariat stratégique complet ? Ce parcours vous donne gouvernance, planification, ET support. C\'est comme avoir un conseil d\'administration, un plan d\'affaires, ET une équipe opérationnelle. Vous bénéficiez d\'un partenariat stratégique complet avec transformation mesurable.',
    pourQui: 'Entreprises qui veulent professionnaliser complètement leurs TI. Idéal pour les entreprises qui veulent être au niveau des grandes entreprises.',
    objections: [
      {
        objection: '"C\'est trop pour nous"',
        reponse: 'Je comprends ! Mais regardez : les grandes entreprises ont gouvernance, planification, ET support. Pourquoi pas vous ? Ce parcours vous donne le même niveau de professionnalisme. Et vous voyez votre évolution année sur année avec des ajustements proactifs.'
      },
      {
        objection: '"On veut juste le support"',
        reponse: 'Je comprends ! Mais le support sans vision stratégique, c\'est comme conduire sans destination. Ce parcours vous donne les deux : le support pour aujourd\'hui ET la vision pour demain. C\'est ça, un vrai partenariat stratégique.'
      }
    ],
    valeur: 'Le client bénéficie d\'un partenariat stratégique complet avec transformation mesurable, ajustements proactifs, et vision claire de son évolution sur plusieurs années. Ses TI sont au niveau des grandes entreprises.'
  }
];

